import React, { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';

interface RevenueData {
  monthlyRevenue: number;
  yearlyRevenue: number;
  monthlyFees: number;
  yearlyFees: number;
}

interface RevenueKPIDashboardProps {
  onNavigate?: (screen: string) => void;
}

const RevenueKPIDashboard: React.FC<RevenueKPIDashboardProps> = ({ onNavigate }) => {
  const [revenueData, setRevenueData] = useState<RevenueData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { get } = useApi();

  useEffect(() => {
    const fetchRevenueData = async () => {
      try {
        setLoading(true);
        const response = await get('/api/admin/kpis/revenue');
        if (response.success) {
          setRevenueData(response.data);
        } else {
          setError(response.message || 'Failed to fetch revenue data');
        }
      } catch (err: any) {
        setError(err.message || 'An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchRevenueData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="p-6">Loading revenue data...</div>;
  if (error) return <div className="p-6 text-red-500">Error: {error}</div>;
  if (!revenueData) return <div className="p-6 text-gray-600">No revenue data available</div>;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount / 100);
  };

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Revenue KPI Dashboard</h2>
        {onNavigate && (
          <button
            type="button"
            onClick={() => onNavigate('admin-quotes-new')}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg shadow-sm"
            data-testid="revenue-kpi-create-quote"
          >
            Create Quote
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Monthly Revenue */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Monthly Revenue</h3>
          <div className="text-3xl font-bold text-green-600 mb-2">
            {formatCurrency(revenueData.monthlyRevenue)}
          </div>
          <p className="text-sm text-gray-600">Total revenue this month</p>
        </div>

        {/* Yearly Revenue */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Yearly Revenue</h3>
          <div className="text-3xl font-bold text-green-600 mb-2">
            {formatCurrency(revenueData.yearlyRevenue)}
          </div>
          <p className="text-sm text-gray-600">Total revenue this year</p>
        </div>

        {/* Monthly Platform Fees */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Monthly Platform Fees</h3>
          <div className="text-3xl font-bold text-blue-600 mb-2">
            {formatCurrency(revenueData.monthlyFees)}
          </div>
          <p className="text-sm text-gray-600">Platform fees collected this month</p>
        </div>

        {/* Yearly Platform Fees */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Yearly Platform Fees</h3>
          <div className="text-3xl font-bold text-blue-600 mb-2">
            {formatCurrency(revenueData.yearlyFees)}
          </div>
          <p className="text-sm text-gray-600">Platform fees collected this year</p>
        </div>
      </div>

      {/* Platform Fee Analysis */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Platform Fee Rate</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">This month:</span>
              <span className="font-semibold text-blue-600">
                {revenueData.monthlyRevenue > 0
                  ? ((revenueData.monthlyFees / revenueData.monthlyRevenue) * 100).toFixed(1) + '%'
                  : '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">This year:</span>
              <span className="font-semibold text-blue-600">
                {revenueData.yearlyRevenue > 0
                  ? ((revenueData.yearlyFees / revenueData.yearlyRevenue) * 100).toFixed(1) + '%'
                  : '—'}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Contractor Payouts</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">This month:</span>
              <span className="font-semibold text-gray-800">
                {formatCurrency(revenueData.monthlyRevenue - revenueData.monthlyFees)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">This year:</span>
              <span className="font-semibold text-gray-800">
                {formatCurrency(revenueData.yearlyRevenue - revenueData.yearlyFees)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueKPIDashboard;

